/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useEffect, useState } from "react";
import { DocumentSelector } from "cms/documents/document-selector";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { isStringNotEmpty } from "utils/empty-exists";
import { AddDocumentButton } from "./add-document-button";
import { DocumentDisplay } from "./document-display";
import { cloneObjectsArray } from "utils/array";

const CardDocumentSelector = React.memo((props) => {
    const { card, index, cardsList, cardsListRef, updateCards } = props;
    const { token } = useAuth();
    
    const documentGuid = card?.document || "";
    const [localDocument, setLocalDocument] = useState();

    // Load document from guid on mount and when guid changes
    useEffect(() => {
        const loadDocument = async () => {
            if (isStringNotEmpty(documentGuid) && documentGuid.length > 30) {
                try {
                    const docObj = await SimpliCityAdminSDK.document.findOne(token, documentGuid);
                    setLocalDocument(docObj);
                } catch (err) {
                    setLocalDocument(undefined);
                }
            } else {
                setLocalDocument(undefined);
            }
        };
        loadDocument();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentGuid, token]);

    const handleSaveDocument = (doc) => {
        setLocalDocument(doc);
        const currentCardsList = cardsListRef?.current || cardsList;
        if (currentCardsList && updateCards && index !== undefined) {
            const updatedCards = cloneObjectsArray(currentCardsList);
            updatedCards[index] = {
                ...card,
                document: doc?.guid || "",
            };
            updateCards(updatedCards);
        }
    };

    const handleSelectDocument = (doc) => {
        setLocalDocument(doc);
        const currentCardsList = cardsListRef?.current || cardsList;
        if (currentCardsList && updateCards && index !== undefined) {
            const updatedCards = cloneObjectsArray(currentCardsList);
            updatedCards[index] = {
                ...card,
                document: doc?.guid || "",
            };
            updateCards(updatedCards);
        }
    };

    const handleChooseDocument = (doc) => {
        if (!doc) {
            setLocalDocument(undefined);
            const currentCardsList = cardsListRef?.current || cardsList;
            if (currentCardsList && updateCards && index !== undefined) {
                const updatedCards = cloneObjectsArray(currentCardsList);
                updatedCards[index] = {
                    ...card,
                    document: "",
                };
                updateCards(updatedCards);
            }
            return;
        }
        handleSelectDocument(doc);
    };

    const DocumentDisplayWrapper = (displayProps) => {
        return (
            <DocumentDisplay
                {...displayProps}
                document={displayProps.document || localDocument}
                card={card}
                index={index}
                cardsList={cardsList}
                cardsListRef={cardsListRef}
                updateCards={updateCards}
            />
        );
    };

    return (
        <DocumentSelector
            document={localDocument}
            setDocument={setLocalDocument}
            onSaveDocumentHandler={handleSaveDocument}
            onSelectDocumentHandler={handleSelectDocument}
            onChooseDocumentHandler={handleChooseDocument}
            AddComponent={AddDocumentButton}
            DisplayComponent={DocumentDisplayWrapper}
        />
    );
}, (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    // Excluded title and description to prevent rerenders when only those change
    return (
        prevProps.index === nextProps.index &&
        prevProps.card?.document === nextProps.card?.document &&
        prevProps.card?.banner_image?.image === nextProps.card?.banner_image?.image
    );
});

CardDocumentSelector.displayName = "CardDocumentSelector";
export { CardDocumentSelector };
